import { useQueryData } from "@src/hooks/useQueryData";
import { byCode, reloadAndReplaceCachedListItem } from "@src/hooks/queryListCache";
import {
    stockActInvsServices,
    type StockActInv,
    type StockActInvContent,
    type StockActInvContentExtends,
    type StockActInvExtends,
} from "../../../../api/stockActInvs";

const FULL_INVENTORY_CARD_INCLUDES = {
    include: ["Division", "State"] satisfies Array<keyof StockActInvExtends>,
};

const FULL_INVENTORY_CONTENT_INCLUDES = {
    include: ["DepotCard", "StockObj", "Measure", "Lot", "Order", "PlacedCellRemainders"] satisfies Array<
        keyof StockActInvContentExtends
    >,
};

/** Загружает карточку АИ для полной инвентаризации склада. */
export function useStockFullInventoryCardQuery(actCode: number) {
    return useQueryData<StockActInv | null>({
        queryKey: ["stock", "fullInventory", actCode, "card"],
        queryFn: ({ signal }) =>
            stockActInvsServices.loadByCode(
                {
                    code: actCode,
                    ...FULL_INVENTORY_CARD_INCLUDES,
                },
                { signal },
            ),
        enabled: actCode > 0,
        meta: { errorSource: "stock.fullInventory.card" },
    });
}

/** Результат загрузки строк АИ для полной инвентаризации склада. */
export interface UseStockFullInventoryContentsQueryResult {
    /** Строки состава АИ. */
    content: StockActInvContent[];
    /** Признак фоновой загрузки. */
    isFetching: boolean;
    /** Перезагружает строки состава АИ. */
    refetch: () => Promise<unknown>;
    /**
     * Перезагружает одну строку состава АИ и заменяет её в query cache.
     * @param contentCode код строки состава АИ
     */
    refetchContentByCode: (contentCode: number) => Promise<void>;
}

/** Загружает строки АИ для полной инвентаризации склада. */
export function useStockFullInventoryContentsQuery(actCode: number): UseStockFullInventoryContentsQueryResult {
    const query = useQueryData<StockActInvContent[]>({
        queryKey: ["stock", "fullInventory", actCode, "contents"],
        queryFn: ({ signal }) =>
            stockActInvsServices.content.load(
                {
                    filter: { ActCode: actCode },
                    ...FULL_INVENTORY_CONTENT_INCLUDES,
                },
                { signal },
            ),
        enabled: actCode > 0,
        meta: { errorSource: "stock.fullInventory.contents" },
    });

    const refetchContentByCode = async (contentCode: number): Promise<void> => {
        await reloadAndReplaceCachedListItem({
            setData: query.setData,
            getKey: byCode,
            loadItem: () =>
                stockActInvsServices.content.loadByCode({
                    code: contentCode,
                    actCode,
                    ...FULL_INVENTORY_CONTENT_INCLUDES,
                }),
        });
    };

    return {
        content: query.data ?? [],
        isFetching: query.isFetching,
        refetch: query.refetch,
        refetchContentByCode,
    };
}
